import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';

function Classification() {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [processingStatus, setProcessingStatus] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please upload a valid Excel file (.xlsx or .xls)');
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 10MB for safety)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert(`File is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 10MB.\n\nPlease use a smaller file or contact support for larger files.`);
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      setDownloadLinks([]); // Clear previous results
      setProcessingStatus('');
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:*/*;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert("Please select an Excel file before submitting.");
      return;
    }

    setIsLoading(true);
    setProcessingStatus('Uploading file...');
    
    try {
      // Convert file to base64
      const fileContent = await convertFileToBase64(selectedFile);
      
      setProcessingStatus('Processing tribes and generating reports...');
      
      // Call Lambda function via API Gateway
      const data = await apiCall("/tribe-classification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          file_content: fileContent,
          filename: selectedFile.name,
        }),
      });
      
      if (data.error) {
        const errorDetails = data.details ? `\n\nDetails: ${data.details}` : '';
        const errorHint = data.hint ? `\n\nHint: ${data.hint}` : '';
        alert(`Error: ${data.error}${errorDetails}${errorHint}`);
        setProcessingStatus(`Error: ${data.error}`);
      } else {
        setDownloadLinks(data.download_links || []);
        setProcessingStatus(
          `Successfully processed ${data.tribes_processed} tribe(s). Reports are ready for download.`
        );
        console.log("Classification result:", data);
      }
    } catch (error) {
      console.error("Classification Error:", error);
      
      // Show detailed error message
      let errorMessage = error.message;
      if (error.message.includes('504')) {
        errorMessage = 'Request timeout. The file may be too large or processing is taking too long. Please try with a smaller file or contact support.';
      } else if (error.message.includes('502')) {
        errorMessage = 'Server error. The Lambda function may have crashed. Please check CloudWatch logs or contact support.';
      } else if (error.message.includes('403')) {
        errorMessage = 'Access denied. You may not have permission to use this feature.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Unauthorized. Please log in again.';
      }
      
      alert(`An error occurred during classification:\n\n${errorMessage}`);
      setProcessingStatus(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = (url, filename) => {
    try {
      console.log('Downloading:', filename, 'from presigned URL');
      
      // For presigned S3 URLs, open directly in new tab
      // The browser will handle the download based on content-type
      window.open(url, '_blank');
      
      console.log('Download initiated:', filename);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download ${filename}. Please try again or contact support.\n\nError: ${error.message}`);
    }
  };

  const handleCancel = () => {
    // Reset form and navigate back
    setSelectedFile(null);
    setDownloadLinks([]);
    setProcessingStatus('');
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="container mt-4">
      <h1>Tribe Classification Reports</h1>
      <p className="text-muted">Upload an Excel file with incident data to generate tribe-specific reports</p>

      <form id="classificationForm" className="card p-4 mb-4" onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="excel_file" className="form-label">
            Select Excel File: <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            id="excel_file"
            className="form-control"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={isLoading}
            required
          />
          <small className="form-text text-muted">
            Upload an Excel file (.xlsx or .xls) containing incident data with "Assignment group" column (max 10MB)
          </small>
          {selectedFile && (
            <div className="mt-2">
              <span className="badge bg-info">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </span>
            </div>
          )}
        </div>
        
        {processingStatus && (
          <div className={`alert ${downloadLinks.length > 0 ? 'alert-success' : 'alert-info'} mb-3`} role="alert">
            {processingStatus}
          </div>
        )}
        
        <div className="d-flex gap-2">
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={isLoading || !selectedFile}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              'Generate Reports'
            )}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Download Links Section */}
      {downloadLinks.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-3">Download Reports</h3>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Tribe</th>
                  <th>PDF Report</th>
                  <th>CSV Data</th>
                </tr>
              </thead>
              <tbody>
                {downloadLinks.map((link, index) => (
                  <tr key={index}>
                    <td className="align-middle">
                      <strong>{link.tribe}</strong>
                    </td>
                    <td className="align-middle">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDownload(link.pdf_url, link.pdf_filename)}
                      >
                        <i className="bi bi-file-pdf me-1"></i>
                        Download PDF
                      </button>
                    </td>
                    <td className="align-middle">
                      {link.csv_url ? (
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => handleDownload(link.csv_url, link.csv_filename)}
                        >
                          <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                          Download CSV
                        </button>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="alert alert-warning mt-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Note:</strong> Download links expire after 1 hour. Please download all files you need.
          </div>
        </div>
      )}
    </div>
  );
}

export default Classification;